import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import DateTimeSelector from '@/components/booking/DateTimeSelector';
import { generateTimeSlots, getDisabledDates, TimeSlot } from '@/utils/timeSlotUtils';
import { getUserTimezone } from '@/utils/timeZoneUtils';
import { format } from 'date-fns';

type Booking = {
  id: string;
  name: string;
  email: string;
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  notes?: string;
  user_id: string;
  booking_link_id: string;
  status: 'confirmed' | 'cancelled' | 'rescheduled';
  created_at: string;
  meeting_type?: string;
  payment_amount?: number;
  payment_currency?: string;
  payment_status?: string;
};

type BookingLink = {
  id: string;
  name: string;
  duration: number;
  availability: Record<string, { enabled: boolean; startTime: string; endTime: string }>;
};

interface RescheduleModalProps {
  booking: Booking;
  isOpen: boolean;
  onClose: () => void;
  onReschedule: () => void;
}

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  booking,
  isOpen,
  onClose,
  onReschedule
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [timezone, setTimezone] = useState<string>(booking.timezone || getUserTimezone());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingLink, setBookingLink] = useState<BookingLink | null>(null);

  // Fetch the booking link details to get duration and availability
  useEffect(() => {
    if (isOpen && booking?.user_id) {
      const fetchBookingLink = async () => {
        const { data, error } = await supabase
          .from('booking_links')
          .select('*')
          .eq('id', booking.booking_link_id)
          .single();

        if (error) {
          console.error('Error fetching booking link:', error);
          toast.error('Could not load booking information');
          return;
        }

        if (data) {
          setBookingLink(data as unknown as BookingLink);
        }
      };

      fetchBookingLink();
    }
  }, [isOpen, booking]);

  // Generate time slots when date changes
  useEffect(() => {
    if (selectedDate && bookingLink) {
      const slots = generateTimeSlots(
        selectedDate,
        bookingLink.duration,
        bookingLink.availability
      );
      setTimeSlots(slots);
      setSelectedTimeSlot(null);
    }
  }, [selectedDate, bookingLink]);

  // Reset time slot selection when timezone changes
  useEffect(() => {
    if (selectedDate && bookingLink) {
      const slots = generateTimeSlots(
        selectedDate,
        bookingLink.duration,
        bookingLink.availability
      );
      setTimeSlots(slots);
      setSelectedTimeSlot(null);
    }
  }, [timezone]);

  const handleReschedule = async () => {
    if (!selectedTimeSlot || !selectedDate) {
      toast.error('Please select a new date and time');
      return;
    }

    setIsSubmitting(true);

    try {
      // Update the booking record
      const { error } = await supabase
        .from('bookings')
        .update({
          date: selectedDate.toISOString().split('T')[0],
          start_time: selectedTimeSlot.startTime.toTimeString().split(' ')[0],
          end_time: selectedTimeSlot.endTime.toTimeString().split(' ')[0],
          timezone: timezone,
          status: 'rescheduled'
        })
        .eq('id', booking.id);

      if (error) {
        throw error;
      }

      // Send rescheduling notification email
      await sendReschedulingEmail({
        ...booking,
        date: selectedDate.toISOString().split('T')[0],
        start_time: selectedTimeSlot.startTime.toTimeString().split(' ')[0],
        end_time: selectedTimeSlot.endTime.toTimeString().split(' ')[0],
        timezone: timezone
      });

      toast.success('Appointment rescheduled successfully');
      onReschedule();
      onClose();
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      toast.error('Failed to reschedule appointment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendReschedulingEmail = async (updatedBooking: Booking) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-booking-confirmation', {
        body: { 
          bookingData: updatedBooking,
          isRescheduled: true
        }
      });
      
      if (error) {
        console.error('Error sending rescheduling email:', error);
      }
    } catch (error) {
      console.error('Error invoking email function:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Reschedule Appointment</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-muted-foreground mb-4">
            Current appointment: {booking ? format(new Date(`${booking.date}T${booking.start_time}`), 'PPP') : ''} at {booking?.start_time.substring(0, 5)}
          </p>
          
          {bookingLink ? (
            <DateTimeSelector
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              timeSlots={timeSlots}
              selectedTimeSlot={selectedTimeSlot}
              setSelectedTimeSlot={setSelectedTimeSlot}
              disabledDates={bookingLink ? getDisabledDates(bookingLink.availability) : undefined}
              timezone={timezone}
              setTimezone={setTimezone}
            />
          ) : (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleReschedule} 
            disabled={!selectedDate || !selectedTimeSlot || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Rescheduling...
              </>
            ) : (
              'Confirm Reschedule'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleModal;
